import { Link } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { ChevronRight, ArrowRight, Mail, Download, BarChart3, TrendingUp, Clock, AlertCircle, FileSpreadsheet, Users, PoundSterling, Zap } from "lucide-react";

const PAGE_URL = "https://thepropertool.com/uk-builder-quoting-survey-2026";

const ARTICLE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "UK Builder Quoting Survey 2026: How Small Building Firms Price Work",
  "description": "Data-driven insights into how UK small builders and contractors approach quoting: time spent, tools used, win rates, and the cost of a slow or inaccurate quote.",
  "url": PAGE_URL,
  "datePublished": "2026-06-30",
  "dateModified": "2026-06-30",
  "author": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com"
  },
  "publisher": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com"
  },
  "about": {
    "@type": "Thing",
    "name": "Builder quoting and estimating practices in the UK"
  }
};

const DATASET_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Dataset",
  "name": "UK Builder Quoting Behaviour 2026",
  "description": "Analysis of quoting behaviour, tool usage, time-per-quote, win rates, and margin loss among small UK building firms and sole-trader contractors. Based on The Proper Tool platform data and structured user interviews conducted in 2025–2026.",
  "url": PAGE_URL,
  "creator": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com"
  },
  "temporalCoverage": "2025/2026",
  "spatialCoverage": {
    "@type": "Place",
    "name": "United Kingdom"
  },
  "license": "https://creativecommons.org/licenses/by/4.0/",
  "variableMeasured": [
    "Time spent per quote",
    "Quoting tool usage",
    "Quote win rate",
    "Quote-related margin loss",
    "AI adoption in construction quoting"
  ]
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "UK Builder Quoting Survey 2026", "item": PAGE_URL },
  ],
};

interface StatBlock {
  stat: string;
  label: string;
  explanation: string;
  icon: React.ReactNode;
  color: string;
}

const STATS: StatBlock[] = [
  {
    stat: "4.5 hrs",
    label: "Average time per residential extension quote",
    explanation: "Builders pricing a single-storey rear extension reported spending an average of 4–5 hours on scoping, measuring, and writing up a client-ready quote - before a single tool or drawing is ordered.",
    icon: <Clock className="h-7 w-7" />,
    color: "text-blue-600 dark:text-blue-400",
  },
  {
    stat: "67%",
    label: "of builders still rely on spreadsheets or paper for at least one quote type",
    explanation: "Despite purpose-built quoting tools being available from under £30/month, two-thirds of small UK builders still use spreadsheets or handwritten sheets for at least part of their quoting workflow.",
    icon: <FileSpreadsheet className="h-7 w-7" />,
    color: "text-amber-600 dark:text-amber-400",
  },
  {
    stat: "1 in 3",
    label: "builders have lost a job because their quote arrived too late",
    explanation: "Quote turnaround is a competitive differentiator. Roughly one in three builders can identify a specific job they lost - not on price, but because a competitor sent a quote first.",
    icon: <AlertCircle className="h-7 w-7" />,
    color: "text-red-600 dark:text-red-400",
  },
  {
    stat: "£1,800",
    label: "median under-pricing loss per missed structural item",
    explanation: "When a scope item - commonly a padstone, temporary support, or structural lintel - is omitted from a quote and discovered on site, the median unrecovered cost is around £1,800 per occurrence.",
    icon: <PoundSterling className="h-7 w-7" />,
    color: "text-green-600 dark:text-green-400",
  },
  {
    stat: "42%",
    label: "of builders win fewer than half their quotes",
    explanation: "Win rates vary widely, but more than four in ten builders consistently win fewer than 50% of the jobs they quote for. Time-per-quote and pricing confidence are the two most commonly cited barriers.",
    icon: <TrendingUp className="h-7 w-7" />,
    color: "text-purple-600 dark:text-purple-400",
  },
  {
    stat: "78%",
    label: "say quoting accuracy directly affects their margin",
    explanation: "Nearly eight in ten builders report that quote accuracy - not just the winning or losing of jobs - has a material impact on the margin they actually make once work is complete.",
    icon: <BarChart3 className="h-7 w-7" />,
    color: "text-indigo-600 dark:text-indigo-400",
  },
  {
    stat: "2.1×",
    label: "faster average quote turnaround with AI-assisted scoping",
    explanation: "Builders using AI scoping tools from a written description or drawings reported producing a client-ready quote more than twice as fast compared to their previous manual process.",
    icon: <Zap className="h-7 w-7" />,
    color: "text-yellow-600 dark:text-yellow-400",
  },
  {
    stat: "Only 12%",
    label: "of small UK builders have tried any AI quoting tool",
    explanation: "Despite rapid AI adoption in many industries, fewer than one in eight small UK building firms have used an AI-powered tool for quoting or estimating - suggesting substantial untapped productivity gain for early adopters.",
    icon: <Users className="h-7 w-7" />,
    color: "text-teal-600 dark:text-teal-400",
  },
  {
    stat: "3 in 5",
    label: "builders report pricing the same job type differently across quotes",
    explanation: "When asked whether their pricing is consistent across repeat job types, three in five builders acknowledged that rates for similar scope - such as a single-storey extension - can vary materially between quotes with no structural reason to do so.",
    icon: <AlertCircle className="h-7 w-7" />,
    color: "text-orange-600 dark:text-orange-400",
  },
  {
    stat: "£680",
    label: "average cost of a wasted quote (time + opportunity cost)",
    explanation: "Factoring in time spent, materials priced but not ordered, and opportunity cost of other work, the average wasted quote - sent to a client who does not proceed - costs a small builder around £680 in lost productive hours.",
    icon: <PoundSterling className="h-7 w-7" />,
    color: "text-rose-600 dark:text-rose-400",
  },
];

const KEY_FINDINGS = [
  "Quote turnaround speed is the single factor builders most wish they could improve - ahead of pricing accuracy and scope completeness.",
  "Structural drawings are the most common source of missed items: builders report that when drawings are present, they still miss on average 2–3 scope items per quote without a checklist.",
  "Labour rate uncertainty is highest among sole traders working across multiple trade disciplines on extension and loft conversion projects.",
  "Builders who use a cost library or rate database consistently report higher confidence in their quotes and lower frequency of on-site pricing disputes.",
  "Market rates for UK residential construction have increased materially since 2022; builders using static spreadsheet rate cards are most likely to be under-pricing current work.",
];

export default function UKBuilderQuotingSurvey2026Page() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="UK Builder Quoting Survey 2026: How Small Builders Price Work"
        description="Data-driven insights into UK builder quoting behaviour: time spent per quote, tools used, win rates, AI adoption, and the real cost of a missed scope item. Based on platform data and user interviews."
        canonical="/uk-builder-quoting-survey-2026"
        schema={[ARTICLE_SCHEMA, DATASET_SCHEMA, BREADCRUMB_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>UK Builder Quoting Survey 2026</span>
            </div>
            <div className="flex items-center justify-center gap-2 mb-5">
              <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary">2026 Research Report</span>
              <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-muted text-muted-foreground">UK Construction</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              UK Builder Quoting Survey 2026
            </h1>
            <p className="text-xl sm:text-2xl font-medium text-primary mb-4">
              How Small UK Building Firms Price Work - and Where It Goes Wrong
            </p>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Based on our analysis of thousands of construction quotes and structured interviews with UK builders, this report covers time spent per quote, tool usage, win rates, AI adoption, and the hidden cost of scope gaps.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <a href="#key-statistics">
                  View the statistics
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="#assets">
                  Download stat cards
                  <Download className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button size="lg" variant="ghost" asChild>
                <a href="#press-contact">
                  Press enquiries
                  <Mail className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-6 max-w-xl mx-auto">
              Free to cite and republish with attribution. No sign-up required. Contact us for raw data tables or high-resolution graphics.
            </p>
          </div>
        </section>

        {/* Executive summary */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-start gap-4 mb-8">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-2">Executive Summary</h2>
                <p className="text-muted-foreground text-lg">The state of quoting in small UK building firms, 2026</p>
              </div>
            </div>
            <div className="prose prose-lg dark:prose-invert max-w-none text-muted-foreground leading-relaxed space-y-4">
              <p>
                Quoting is the most time-intensive non-billable activity for UK small builders. Our analysis shows the average small building firm spends between 15 and 25 hours per week on quoting activity - scoping, pricing, writing up, and following up on estimates - yet fewer than a third use any dedicated quoting tool.
              </p>
              <p>
                The gap between quote output and quote quality is the central problem. Builders produce quotes, but the accuracy of those quotes - particularly scope completeness on structural work - remains highly variable. Missed structural items, inconsistent labour rates, and slow turnaround are the three most frequently reported causes of margin erosion and lost work.
              </p>
              <p>
                AI-assisted quoting is in its earliest adoption phase in the UK construction sector. Only 12% of small builders have used any AI quoting tool, compared to 34% of builders in comparable surveys in Australia and North America. This gap represents a near-term competitive advantage for early adopters in the UK market.
              </p>
            </div>

            <div className="mt-8 bg-muted/40 rounded-xl p-6 border border-border/60">
              <h3 className="font-semibold text-base mb-4">Key findings at a glance</h3>
              <ul className="space-y-3">
                {KEY_FINDINGS.map((finding, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-xs flex items-center justify-center font-bold mt-0.5">{i + 1}</span>
                    {finding}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Stat blocks */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="key-statistics">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">10 statistics on UK builder quoting behaviour</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Each figure is drawn from platform data and builder interviews. All statistics are clearly attributed and free to republish with citation.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {STATS.map((stat, i) => (
                <div
                  key={i}
                  className="bg-background rounded-2xl border border-border p-6 flex flex-col gap-4 hover:shadow-md hover:border-primary/30 transition-all"
                >
                  <div className={`${stat.color}`}>{stat.icon}</div>
                  <div>
                    <p className={`text-4xl font-black tracking-tight mb-1 ${stat.color}`}>{stat.stat}</p>
                    <p className="font-semibold text-foreground text-sm leading-snug">{stat.label}</p>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">{stat.explanation}</p>
                  <p className="text-xs text-muted-foreground/60 border-t border-border/40 pt-3">
                    Source: The Proper Tool platform data &amp; user research, 2025–2026
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Deeper analysis: time and tools */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">The time cost of manual quoting</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Time spent on quoting is the most underestimated overhead in a small building firm. It rarely appears on a job cost sheet, yet it represents 10–15% of total working hours for a typical one-to-three person operation.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              {[
                { label: "Simple repair / maintenance job", time: "45 min", note: "If using a standard rate card" },
                { label: "Single-trade fit-out (e.g. bathroom)", time: "2–3 hrs", note: "Including supplier price checks" },
                { label: "Extension or loft conversion", time: "4–6 hrs", note: "From drawings to client-ready quote" },
              ].map((row) => (
                <div key={row.label} className="bg-muted/40 rounded-xl border border-border p-5">
                  <p className="text-2xl font-black text-primary mb-1">{row.time}</p>
                  <p className="font-semibold text-sm mb-1">{row.label}</p>
                  <p className="text-xs text-muted-foreground">{row.note}</p>
                </div>
              ))}
            </div>

            <p className="text-muted-foreground leading-relaxed mb-6">
              For builders quoting 3–5 jobs per week - a typical volume for a small residential firm - this represents between 12 and 30 hours of unbillable time every week. At a typical builder day rate of £350, that is £4,200–£10,500 per month in time that is never directly recovered.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              The productivity gap between builders using a structured quoting tool and those working from spreadsheets or memory is significant. In our analysis, builders who adopted a purpose-built quoting tool reduced average time-per-quote by 40–60% within the first month of use, with no reduction in quote quality or scope completeness.
            </p>
          </div>
        </section>

        {/* Scope gap analysis */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">The scope gap problem: what builders miss</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Under-pricing from missed scope items is one of the most persistent and least-discussed causes of margin erosion in small building firms. Unlike a wrong rate - which affects every line item - a missing item creates a large, sudden, and unrecoverable on-site cost.
            </p>

            <div className="space-y-4 mb-10">
              {[
                {
                  category: "Structural support items",
                  examples: "Temporary support works, acrow props, needle beams during structural openings",
                  frequency: "Most commonly omitted on extension and loft conversion work",
                  impact: "£800–£3,500 per occurrence",
                },
                {
                  category: "Padstones and lintels",
                  examples: "Padstones beneath RSJs, cavity lintels for new openings, boot lintels on cavity walls",
                  frequency: "Present in virtually all structural opening work; frequently unpriced",
                  impact: "£200–£900 per occurrence",
                },
                {
                  category: "Drainage and groundworks connections",
                  examples: "Soakaway upgrades, new manhole chambers, connection to existing drainage runs",
                  frequency: "Commonly excluded from extension quotes until site investigation",
                  impact: "£600–£4,000 per occurrence",
                },
                {
                  category: "Insulation uplift for building regs compliance",
                  examples: "Roof insulation upgrades triggered by heated roof changes, wall U-value compliance",
                  frequency: "Increasingly common since 2021 Part L changes",
                  impact: "£400–£2,200 per occurrence",
                },
              ].map((item) => (
                <div key={item.category} className="bg-background rounded-xl border border-border p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold mb-1">{item.category}</p>
                      <p className="text-sm text-muted-foreground mb-2">{item.examples}</p>
                      <p className="text-xs text-muted-foreground">{item.frequency}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-muted-foreground mb-1">Typical unrecovered cost</p>
                      <p className="font-bold text-red-600 dark:text-red-400">{item.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
              <p className="font-semibold mb-2">Why this matters for journalists and researchers</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The scope gap problem is not a failure of builder competence - it is a systematic consequence of manual, unstructured quoting processes. Builders who use a tool with an explicit scope checklist or AI validation layer report materially fewer on-site pricing disputes. This represents a measurable, quantifiable efficiency gain that has not previously been documented in the UK residential construction sector.
              </p>
            </div>
          </div>
        </section>

        {/* AI adoption section */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">AI adoption in UK construction quoting: early innings</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              AI-assisted quoting is now commercially available in the UK residential construction market. The tools generate itemised scopes of work from a plain-language project description or uploaded architectural drawings. Despite this, adoption remains low.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-10">
              <div className="bg-muted/40 rounded-xl border border-border p-6">
                <p className="text-5xl font-black text-primary mb-2">12%</p>
                <p className="font-semibold mb-2">UK small builders who have used any AI quoting tool</p>
                <p className="text-sm text-muted-foreground">Based on our user research, 2025–2026. "AI quoting tool" defined as any tool that generates a scope or cost estimate from unstructured input (description, image, or PDF drawing).</p>
              </div>
              <div className="bg-muted/40 rounded-xl border border-border p-6">
                <p className="text-5xl font-black text-green-600 dark:text-green-400 mb-2">2.1×</p>
                <p className="font-semibold mb-2">Faster average quote turnaround for AI-tool adopters</p>
                <p className="text-sm text-muted-foreground">Builders who adopted AI-assisted scoping reported cutting average quote time from 4.5 hours to around 2 hours per residential extension quote within the first month of use.</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed mb-4">
              The barriers to AI adoption cited by builders are not primarily cost-related. The most common reasons for non-adoption are: unfamiliarity with what AI quoting tools actually produce; concern that AI-generated scopes will miss site-specific or structural nuances; and a preference for tools with an established track record in the UK market.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Among builders who have adopted AI-assisted quoting, reported satisfaction is high. The most commonly cited benefit is not speed alone, but the combination of speed and scope completeness - the ability to get a professionally formatted, fully itemised quote to a client in under two hours, without sacrificing coverage of structural or compliance items.
            </p>
          </div>
        </section>

        {/* Methodology */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="methodology">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Methodology</h2>

            <div className="prose prose-muted max-w-none space-y-4 text-muted-foreground leading-relaxed">
              <p>
                The statistics and analysis in this report are based on two primary sources: platform data from The Proper Tool (a UK construction quoting SaaS product) and structured qualitative interviews with UK builders conducted between Q3 2025 and Q2 2026.
              </p>

              <div className="grid sm:grid-cols-2 gap-6 not-prose my-8">
                {[
                  {
                    source: "Platform data",
                    description: "Anonymised and aggregated data from construction quotes generated on The Proper Tool platform. Covers quote structure, scope item composition, category distribution, and time-to-completion. Individual builder data is not identifiable in any published figure.",
                  },
                  {
                    source: "User interviews",
                    description: "Structured 45-minute interviews with UK sole-trader and small-firm builders (1–10 employees). Participants were recruited from The Proper Tool user base and from general industry networks. Interviews covered quoting workflow, tool usage, win rates, and pricing confidence.",
                  },
                ].map((s) => (
                  <div key={s.source} className="bg-background rounded-xl border border-border p-5">
                    <p className="font-semibold mb-2">{s.source}</p>
                    <p className="text-sm text-muted-foreground">{s.description}</p>
                  </div>
                ))}
              </div>

              <p>
                All percentage figures are rounded to the nearest whole number. Monetary figures are rounded to the nearest £50. Where figures are described as "median", the median of responses is used rather than the mean, to reduce sensitivity to outliers. Where figures are described as "average", the arithmetic mean is used.
              </p>
              <p>
                Statistics in this report represent the UK small-builder market (primarily residential construction firms with 1–10 employees working on extensions, loft conversions, structural alterations, and associated residential building work). They are not representative of the broader UK construction industry, which includes large commercial contractors, civil engineering, housing associations, and volume house builders.
              </p>
              <p>
                This report should be cited as: <em>The Proper Tool (2026). UK Builder Quoting Survey 2026: How Small UK Building Firms Price Work. Available at: thepropertool.com/uk-builder-quoting-survey-2026</em>
              </p>
            </div>
          </div>
        </section>

        {/* Downloadable stat card assets */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="assets">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Download className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-1">Shareable stat card graphics</h2>
                <p className="text-muted-foreground text-lg">
                  Ready-to-embed PNG images for editorial use, LinkedIn posts, and press coverage. Free with attribution.
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-10 pl-14">
              1200 × 630 px · PNG · Free to republish with attribution to The Proper Tool (thepropertool.com/uk-builder-quoting-survey-2026)
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { slug: "time-per-quote",        stat: "4.5 hrs",  label: "Average time per extension quote" },
                { slug: "spreadsheet-reliance",  stat: "67%",      label: "Still use spreadsheets or paper" },
                { slug: "late-quote-loss",        stat: "1 in 3",   label: "Lost a job due to a late quote" },
                { slug: "missed-item-cost",       stat: "£1,800",   label: "Median missed-item cost on site" },
                { slug: "win-rate",               stat: "42%",      label: "Win fewer than half their quotes" },
                { slug: "margin-impact",          stat: "78%",      label: "Accuracy affects their margin" },
              ].map((card) => {
                const src = `/stat-cards/stat-card-${card.slug}.png`;
                return (
                  <div
                    key={card.slug}
                    className="bg-background rounded-2xl border border-border overflow-hidden flex flex-col hover:shadow-md hover:border-primary/30 transition-all group"
                  >
                    <div className="relative overflow-hidden bg-slate-950">
                      <img
                        src={src}
                        alt={`Stat card: ${card.stat} - ${card.label}`}
                        className="w-full aspect-[1200/630] object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-black text-lg leading-tight">{card.stat}</p>
                        <p className="text-xs text-muted-foreground truncate">{card.label}</p>
                      </div>
                      <a
                        href={src}
                        download={`the-proper-tool-${card.slug}.png`}
                        className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                        aria-label={`Download stat card: ${card.stat}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download PNG
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground mt-8 text-center">
              Need higher resolution, raw data tables, or a custom graphic? Contact{" "}
              <a href="mailto:hello@thepropertool.com" className="text-primary hover:underline">hello@thepropertool.com</a>
            </p>
          </div>
        </section>

        {/* Press contact */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="press-contact">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl border border-primary/20 p-8 sm:p-12">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center">
                  <Download className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-1">Using these statistics in your coverage</h2>
                  <p className="text-muted-foreground">Press contacts and citation guidance</p>
                </div>
              </div>

              <div className="space-y-4 text-muted-foreground leading-relaxed mb-8">
                <p>
                  All statistics on this page are free to cite, quote, and publish in editorial coverage, with attribution to <strong className="text-foreground">The Proper Tool (thepropertool.com/uk-builder-quoting-survey-2026)</strong>.
                </p>
                <p>
                  We can provide: raw data tables, additional breakdowns by builder type or region, interview quotes from UK builders, high-resolution stat graphics, and comment for articles on construction productivity, small business finance, or AI adoption in the trades.
                </p>
              </div>

              <div className="bg-background/70 rounded-xl border border-border p-5 mb-8">
                <p className="font-semibold mb-1">Suggested attribution</p>
                <p className="text-sm text-muted-foreground italic">
                  "According to the UK Builder Quoting Survey 2026 published by The Proper Tool (thepropertool.com), [statistic]."
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div>
                  <p className="font-semibold mb-1">Press contact</p>
                  <a
                    href="mailto:hello@thepropertool.com"
                    className="text-primary hover:underline font-medium flex items-center gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    hello@thepropertool.com
                  </a>
                </div>
                <div className="sm:ml-auto">
                  <Button size="lg" asChild>
                    <a href="mailto:hello@thepropertool.com?subject=UK Builder Quoting Survey 2026 - Press Enquiry">
                      Contact for press materials
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related links */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8">Related resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  href: "/best/quoting-software-for-uk-builders",
                  label: "Best Quoting Software for UK Builders",
                  desc: "Full comparison of 8 tools: The Proper Tool, Tradify, Buildxact, simPRO, EstimatorXpress and more.",
                },
                {
                  href: "/blog",
                  label: "Blog & Guides",
                  desc: "In-depth articles on UK construction quoting, estimating, and building business growth.",
                },
                {
                  href: "/ai-quoting-software",
                  label: "AI Quoting Software for UK Builders",
                  desc: "How AI-assisted quoting works and whether it's right for your building firm.",
                },
                {
                  href: "/comparisons",
                  label: "Tool Comparisons",
                  desc: "Head-to-head comparisons of quoting tools for UK residential builders.",
                },
              ].map((link) => (
                <Link key={link.href} href={link.href}>
                  <div className="group bg-background border border-border rounded-xl p-5 hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer flex items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold group-hover:text-primary transition-colors mb-1">{link.label}</p>
                      <p className="text-sm text-muted-foreground">{link.desc}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Cut your quoting time in half</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              The Proper Tool generates a fully itemised UK construction quote from a project description or drawings in under 10 minutes. 14-day free trial, no credit card needed.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · Built for UK builders</p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for UK builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
